/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment  */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs'
import { filter, finalize, first, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'

import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { DestroyService } from '@core/services/destroy.service'
import { ScheduledReportApiService } from '@app/modules/client/config/scheduled-reports/scheduled-report-api.service'
import { RecipientFormControlLabels } from '@core/constants/notifications'
import { RecipientType, ScopeSubType } from '@shared/modules/notifications/shared/notification.model'
import { AuthService } from '@app/modules/auth/auth.service'
import { Role } from '@shared/models/role.model'
import { NotificationFormService } from '@shared/modules/notifications/notification-form.service'
import { TeamType } from '@shared/models/team.model'
import { RoleService } from '@shared/services/role.service'
import {
  ScheduledReportModel,
  ScheduledReportModelForm,
  ScheduledReportStatus,
  ScheduledReportTemplate,
  ScheduleReportInterval,
} from '@shared/models/scheduled-report.model'
import { ScheduledReportService } from '@app/modules/client/config/scheduled-reports/scheduled-report.service'
import { Recipient, RecipientScopeItem } from '@app/modules/client/shared/pipes/recipients.pipe'
import { handleFormErrors } from '@core/helpers/handle-form-errors'
import { User } from '@shared/models/user.model'
import { DeviceSensorsApiService } from '@shared/services/device-sensors-api.service'
import { MatChipInputEvent, MAT_CHIPS_DEFAULT_OPTIONS } from '@angular/material/chips'
import { COMMA, ENTER, SEMICOLON } from '@angular/cdk/keycodes'
import { GeofencesApiService } from '@app/modules/client/shared/services/geofences-api.service'
import { ReportTypes } from '@app/shared/models/reports/types'
import { Timezone } from '@app/shared/models/timezone.model'
import { DataService } from '@app/shared/services/data.service'
import { ClientsService } from '@app/modules/admin/clients/clients.service'

interface DayFormat {
  id: string
  label: string
}

@Component({
  selector: 'lin-scheduled-report-form',
  templateUrl: './scheduled-report-form.component.html',
  styleUrls: ['./scheduled-report-form.component.scss'],
  providers: [
    GeofencesApiService,
    DestroyService,
    NotificationFormService,
    DeviceSensorsApiService,
    {
      provide: MAT_CHIPS_DEFAULT_OPTIONS,
      useValue: {
        separatorKeyCodes: [ENTER, COMMA, SEMICOLON],
      },
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ScheduledReportFormComponent implements OnInit {
  form: FormGroup
  isSubmitting = false
  isDisabled = false
  isFormChanged: ReturnType<typeof takeFormSnapshot>

  frequencyItems: string[]
  frequencyData: ScheduleReportInterval
  periodItems: string[]
  dayItems: DayFormat[]

  otherEmail: FormControl = new FormControl('')
  otherEmails: string[] = []

  recipientItems: RecipientScopeItem[] = []
  isRecipientsLoaded = true
  roleList: Role[]
  timezones: Timezone[]
  clientTimezoneId: number
  isLoading$ = new BehaviorSubject(false)

  private scheduledReportData: ScheduledReportModel

  readonly RecipientFormControlLabels = RecipientFormControlLabels
  readonly RecipientType = RecipientType

  private readonly listRecipientType = [RecipientType.Role, RecipientType.UserList, RecipientType.UserGroupList]

  constructor(
    public dialogRef: MatDialogRef<ScheduledReportFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ScheduledReportModel,
    private destroyService: DestroyService,
    private fb: FormBuilder,
    private scheduledReportApiService: ScheduledReportApiService,
    private authService: AuthService,
    private readonly notificationFormService: NotificationFormService,
    private cdr: ChangeDetectorRef,
    private roleService: RoleService,
    private readonly clientsService: ClientsService,
    private readonly dataService: DataService,
    private readonly scheduledReportService: ScheduledReportService,
  ) {
    this.scheduledReportData = data
  }

  get user(): User {
    return this.authService.getCurrentUser()
  }

  get frequencyValue(): string | undefined {
    return <string | undefined>this.form.controls.frequency?.value
  }

  get otherEmailIsValid(): boolean {
    const emailIsValid = this.validateEmail(<string>this.otherEmail.value)
    emailIsValid && this.form.pristine && this.form.markAsDirty()

    return emailIsValid
  }

  get isEventLog(): boolean {
    return this.scheduledReportData?.type === ReportTypes.EventLog
  }

  ngOnInit(): void {
    this.initForm()
    this.isLoading$.next(true)
    const client$ = this.clientsService.getClient(this.user?.team?.clientId)
    const timezones$ = this.dataService.getTimezones()

    forkJoin([this.initScheduleTemplate(), timezones$, client$])
      .pipe(takeUntil(this.destroyService))
      .subscribe(([template, timezones, client]) => {
        this.frequencyItems = Object.keys(template.interval.type)
        this.frequencyData = template.interval
        this.timezones = timezones
        this.clientTimezoneId = +client?.timezone
        this.isLoading$.next(false)
        this.fillForm()
      })
  }

  private validateEmail(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return regex.test(email)
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim()

    if (!value || !this.validateEmail(value)) return

    this.otherEmails.push(value)
    event.chipInput.clear()
    this.otherEmail.patchValue('')
  }

  remove(email: string): void {
    this.otherEmails.splice(this.otherEmails.indexOf(email), 1)
    this.form.markAsDirty()
  }

  matchWith(scopeItem: RecipientScopeItem): string {
    return scopeItem.label
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueOf(controlName: string): any {
    const value = this.form.get(controlName).value

    if (value?.subType === ScopeSubType.Any || value === ScopeSubType.Any) return false

    return this.form.get(controlName).value
  }

  submitForm(): void {
    this.isSubmitting = true
    const data = this.prepareDataForSave()

    const reportId = this.scheduledReportData?.id
    const request$ = reportId ? this.scheduledReportApiService.patch(reportId, data) : this.scheduledReportApiService.post(data)

    request$
      .pipe(
        takeUntil(this.destroyService),
        finalize(() => (this.isSubmitting = false)),
      )
      .subscribe(
        () => {
          this.dialogRef.close(true)
        },
        handleFormErrors(this.form),
        () => {
          this.isSubmitting = false
        },
      )
  }

  private isNew(): boolean {
    return !this.scheduledReportData?.id
  }

  private initScheduleTemplate(): Observable<ScheduledReportTemplate> {
    return this.scheduledReportApiService.template()
  }

  private initForm(): void {
    this.form = this.fb.group({
      type: [''],
      title: ['', Validators.required],
      frequency: ['', Validators.required],
      recipientsType: RecipientType.Role,
      recipientsValue: [[], Validators.required],
      format: ['', Validators.required],
      params: [],
      status: [''],
      timezone: '',
    })
    this.frequencyValueChange()
    this.recipientTypeValueChanges()
    this.isFormChanged = takeFormSnapshot(this.form)
  }

  private updateFieldValidity(value: string | string[], field: string) {
    if (value.length) {
      this.form.get(field).clearValidators()
    } else {
      this.form.get(field).setValidators(Validators.required)
    }
    this.form.get(field).updateValueAndValidity()
  }

  private frequencyValueChange(): void {
    this.form.controls.frequency.valueChanges.pipe(takeUntil(this.destroyService)).subscribe((frequency) => {
      this.clearFrequencyFields()
      this.addFrequencyFields(<string>frequency)
    })
  }

  private clearFrequencyFields(): void {
    this.form.removeControl('period')
    this.form.removeControl('day')
    this.form.removeControl('time')
    this.periodItems = []
    this.dayItems = []
  }

  private addFrequencyFields(frequency: string): void {
    this.addPeriodField()

    switch (frequency) {
      case 'daily':
        break
      case 'weekly':
        this.addDayField()
        break
      case 'monthly':
        this.addDayField()
        break
    }

    this.addTimeField()
  }

  private addDayField(): void {
    this.form.addControl('day', new FormControl('', Validators.required))
    this.dayItems = this.dayItemsFormat()
  }

  private addPeriodField(): void {
    this.form.addControl('period', new FormControl('', Validators.required))
    this.periodItems = this.periodItemsFormat()
  }

  private addTimeField(): void {
    this.form.addControl('time', new FormControl('', Validators.required))
  }

  private periodItemsFormat(): string[] {
    return this.frequencyValue ? <string[]>this.frequencyData?.type?.[this.frequencyValue]?.period : []
  }

  private dayItemsFormat(): DayFormat[] {
    const day = this.frequencyData.type[this.frequencyValue]?.day

    if (!day) {
      return []
    }

    switch (this.frequencyValue) {
      case 'monthly':
        return this.toDayFormatFormArray(<string[]>day)
      case 'weekly':
        return this.toDayFormatFromObject(<Record<string | number, string>>day)
      default:
        return []
    }
  }

  private toDayFormatFormArray(dayArray: string[]): DayFormat[] {
    const isArray = Array.isArray(dayArray)
    if (!isArray) {
      return []
    }

    const toDayFormat = (el: string): DayFormat => ({
      id: el,
      label: el,
    })

    return dayArray.map(toDayFormat)
  }

  private toDayFormatFromObject(dayObject: Record<number | string, string>): DayFormat[] {
    const isObject = !Array.isArray(dayObject)
    if (!isObject) {
      return []
    }

    const toDayFormat = (el: string): DayFormat => ({
      id: el,
      label: dayObject[el],
    })

    return Object.keys(dayObject).map(toDayFormat)
  }

  private recipientWithRoles(recipientType: RecipientType): Observable<[RecipientType, Role[], Role[], Role[]]> {
    const eventTeam = this.user.teamType
    const isAdmin: boolean = this.user.isAdmin()

    const clientRoleList$ = eventTeam === TeamType.Client ? this.roleService.clientTeamRoles$.pipe(first()) : of(null)
    const adminRoleList$ = isAdmin ? this.roleService.adminTeamRoles$.pipe(first()) : of(null)
    const resellerRoleList$ = isAdmin ? this.roleService.resellerTeamRoles$.pipe(first()) : of(null)

    return forkJoin([of(recipientType), clientRoleList$, adminRoleList$, resellerRoleList$])
  }

  private recipientItemsList([recipientType, clientRoleList, adminRoleList, resellerRoleList]: [RecipientType, Role[], Role[], Role[]]) {
    const teamId: number = this.user.team.id
    const clientId: number = this.user.team.clientId || this.user.team?.resellerId
    const eventTeam = this.user.teamType
    const roleList = adminRoleList || clientRoleList || resellerRoleList

    return this.notificationFormService.getRecepientScopeItems(
      recipientType,
      teamId,
      clientId,
      roleList,
      eventTeam,
      this.user.teamType,
      this.user.team?.resellerId,
    )
  }

  private recipientTypeValueChanges(): void {
    this.form.controls.recipientsType.valueChanges
      .pipe(
        startWith(this.form.controls.recipientsType.value || RecipientType.Role),
        filter(Boolean),
        tap(() => {
          this.isRecipientsLoaded = false
          this.recipientItems = []
          this.form.controls.recipientsValue.setValue([])
        }),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        switchMap(this.recipientWithRoles.bind(this)),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        switchMap(this.recipientItemsList.bind(this)),
        takeUntil(this.destroyService),
      )
      .subscribe((recipientItems: RecipientScopeItem[]) => {
        const recipient = this.getRecipientList(this.scheduledReportData?.recipients)
        const toId = (value) => value.id
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const scopeValue = this.form.controls?.recipientValue?.value?.map(toId) || recipient?.value?.map(toId)
        this.isRecipientsLoaded = true
        this.recipientItems = recipientItems
        this.setScopeValue(recipientItems, <(string | number)[]>scopeValue, 'recipientsValue')
      })
  }

  private setScopeValue(recipientItems: RecipientScopeItem[], scopeValue: (string | number)[], controlName: string): void {
    this.cdr.markForCheck()
    if (!scopeValue) return
    const isSelected = ({ id }) => scopeValue.includes(<string | number>id)
    const value = recipientItems.filter(isSelected)

    this.form.controls[controlName].setValue(value)
    this.isFormChanged = takeFormSnapshot(this.form)
  }

  private getRecipientList(recipients: Recipient[]): Recipient {
    const isList = (recipient: Recipient) => this.listRecipientType.includes(recipient.type)
    return recipients?.find(isList)
  }

  private fillEditForm(): void {
    this.isLoading$.next(true)
    this.scheduledReportApiService
      .get(this.scheduledReportData.id)
      .pipe(takeUntil(this.destroyService))
      .subscribe((reportData: ScheduledReportModel) => {
        this.scheduledReportData = reportData
        const { type, name, interval, recipients, format, params, status, timezone } = reportData

        const isOtherEmail = (recipient: Recipient) => recipient.type === RecipientType.OtherEmail

        const recipientList = this.getRecipientList(recipients)
        const recipientOther = recipients?.find(isOtherEmail)

        this.otherEmails = <string[]>recipientOther.value

        this.form.patchValue({
          title: name,
          type,
          frequency: interval.type,
          ...(interval.period && { period: interval.period }),
          ...(interval.day && { day: interval.day }),
          ...(interval.time && { time: interval.time }),
          recipientsType: recipientList?.type || RecipientType.Role,
          format,
          params,
          timezone: (<Timezone>timezone)?.id,
          status: status === ScheduledReportStatus.Disabled,
        })
        this.isLoading$.next(false)
      })
  }

  private fillNewForm(): void {
    const { type } = this.scheduledReportData
    const params = { ...this.scheduledReportService.getParams(), ...(this.isEventLog && { eventId: +this.scheduledReportData.eventId }) }

    this.form.patchValue({
      type,
      params,
      timezone: this.clientTimezoneId || '',
    })
  }

  private fillForm(): void {
    if (this.isNew()) {
      this.fillNewForm()
    } else {
      this.fillEditForm()
    }

    this.cdr.detectChanges()
    this.isFormChanged = takeFormSnapshot(this.form)
  }

  private prepareDataForSave(): ScheduledReportModelForm {
    const { type, title, recipientsType, recipientsValue, params, time, day, period, frequency, format, status, timezone } = this.form.value
    const toId = (value) => value.id
    this.otherEmailIsValid && this.otherEmails.push(<string>this.otherEmail.value) && this.otherEmail.patchValue('')

    return {
      type,
      name: title,
      timezone,
      status: status ? ScheduledReportStatus.Disabled : ScheduledReportStatus.Active,
      interval: {
        type: frequency,
        time,
        day,
        period,
      },
      format,
      recipients: {
        type: recipientsType,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        value: recipientsValue.map(toId),
        emails: this.otherEmails,
      },
      params,
    } as ScheduledReportModelForm
  }
}
